import { action, Action, createTransform, persist } from "easy-peasy";
import { omitBy } from "lodash";
import { namesUnknownTalent } from "./unknownVerticals";
import { TalentVertical, User } from "src/graphql/types";
import { isKnownVertical } from "src/utils/user";

export interface JobResubmissionDraft {
  talentUsers: Record<string, User[] | null>;
  setTalentUsersForJob: Action<
    JobResubmissionDraft,
    { jobId: string; users: User[] }
  >;
  existingVerticals: Record<string, TalentVertical[]>;
  setExistingVerticalsForJob: Action<
    JobResubmissionDraft,
    { jobId: string; verticals: TalentVertical[] }
  >;
  resetJob: Action<JobResubmissionDraft, string>;
  reset: Action<JobResubmissionDraft>;
}

// A job whose saved entry names an unknown vertical is dropped whole, so the
// resubmit flow starts again from the job's shortlist on the API: resubmitting
// sends the saved shortlist as the job's whole shortlist.
const dropUnknownVerticals = createTransform(undefined, (data, key) => {
  switch (key) {
    case "talentUsers":
      return omitBy(data, namesUnknownTalent);
    case "existingVerticals":
      return omitBy(data, (verticals?: string[]) =>
        (verticals || []).some((vertical) => !isKnownVertical(vertical)),
      );
    default:
      return data;
  }
});

const persistInLocalStorage = (model: JobResubmissionDraft) =>
  persist(model, {
    storage: "localStorage",
    transformers: [dropUnknownVerticals],
  });

export const jobResubmissionDraft: JobResubmissionDraft = persistInLocalStorage(
  {
    talentUsers: {},
    setTalentUsersForJob: action((state, payload) => {
      state.talentUsers[payload.jobId] = payload.users;
    }),
    resetJob: action((state, jobId) => {
      delete state.talentUsers[jobId];
      delete state.existingVerticals[jobId];
    }),
    existingVerticals: {},
    setExistingVerticalsForJob: action((state, payload) => {
      state.existingVerticals[payload.jobId] = payload.verticals;
    }),
    reset: action((state) => {
      state.talentUsers = {};
      state.existingVerticals = {};
    }),
  },
);
